import { useCallback, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { STRINGS } from '../../../constants';
import OrderDetails from '../../../components/OrderDetails';
import EmptySearchResults from '../../../components/EmptySearchResults';
import Loader from '../../../features/Loader';
import useGetParcelParams from '../../../hooks/useGetParcelParams';
import {
  ERROR_BOUNDARY_TYPES,
  Error,
  useErrorBoundaryConfigurations,
} from '../../../features/ErrorBoundary';
import ShareModal from '../../../components/ShareModal/ShareModal';
import useBreakpoint from '../../../hooks/useBreakpoint';
import parcelSlice from '../../../redux/parcelSlice';

const TrackSection = () => {
  const dispatch = useDispatch();
  const isSmallScreen = useBreakpoint('md');
  const setDrawerOpen = useOutletContext();
  const onBackClick = useCallback(() => {
    setDrawerOpen && setDrawerOpen(false);
  }, []);

  const { parcelCode, parcelNumber, postcodeOrOrderId } = useGetParcelParams();

  const getErrorBoundaryConfig = useErrorBoundaryConfigurations();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const watchParams = useMemo(
    () => [parcelNumber, postcodeOrOrderId],
    [parcelNumber, postcodeOrOrderId]
  );

  const fetchParcelFn = useCallback(
    async ({ parcelCode, postcodeOrOrderId }) => {
      // eslint-disable-next-line no-useless-catch
      try {
        if (parcelCode) {
          return await dispatch(
            parcelSlice.actions.fetchParcelByCode({
              parcelCode,
              postcodeOrOrderId,
            })
          ).unwrap();
        }
      } catch (error) {
        throw error;
      }
    },
    []
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignSelf: 'baseline',
        flexGrow: 1,
        width: '100%',
      }}
    >
      <Loader
        promiseFn={fetchParcelFn}
        parcelCode={parcelCode}
        postcodeOrOrderId={postcodeOrOrderId}
        pendingProps={{
          sx: {
            display: 'flex',
            justifyContent: 'center',
            flexGrow: 1,
            alignItems: 'center',
            p: 2,
            mt: { xs: -4, md: 0 },
          },
        }}
        watch={watchParams}
        errorComponent={error => (
          <Error
            config={getErrorBoundaryConfig[
              ERROR_BOUNDARY_TYPES.WE_HAVE_A_LITTLE_PROBLEM
            ](error.message)}
          />
        )}
      >
        {parcel =>
          parcel ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexGrow: 1,
                p: 2,
              }}
            >
              <ShareModal
                parcel={parcel}
                open={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
              />
              <OrderDetails
                parcel={parcel}
                showBackButton={isSmallScreen}
                footerButtons={
                  <Button
                    variant='outlined'
                    size='medium'
                    onClick={() => setIsShareModalOpen(true)}
                  >
                    {STRINGS.SHARE_TRACKING_INFO}
                  </Button>
                }
                onBackClick={onBackClick}
                styles={{ width: { xs: '100%', lg: '680px' } }}
              />
            </Box>
          ) : (
            <EmptySearchResults sx={{ mt: { xs: 0, md: 5 }, px: 2 }} />
          )
        }
      </Loader>
    </Box>
  );
};

export default TrackSection;
